import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useAuth, useUser } from '@clerk/clerk-react';
import StandardModal, { type MenuButtonType } from '@/components/common/standard.modal';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import EmailIcon from '@mui/icons-material/Email';
import BlockIcon from '@mui/icons-material/Block';
import { updateUserSchemaFactory } from '@/schemas/user/update-user';
import { z } from 'zod';
import { useNotifications } from '@/hooks/use-notifications';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import PasswordResetTab from './tabs/password-reset-tab';
import EmailManagementTab from './tabs/email-management-tab';
import BlockedOrganizationsTab from './tabs/blocked-organizations-tab';
import { useUpdateUserMutation } from '@/state/mutations/users/use-update-user';
import { useUserStore } from '@/state/stores/use-user-store';
import GeneralTab from './tabs/general-tab';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useTranslation } from 'react-i18next';
import type { TranslationKey } from '@/i18n/translation-key';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { ClerkProfilePhotoEditor } from '@/components/nav-bar/clerk-profile-photo-editor';

export type UserSettingsMenuKey = 'general' | 'password' | 'email' | 'blockedOrgs' | 'logout';

const menuButtons: MenuButtonType<UserSettingsMenuKey>[] = [
  { icon: <AccountCircleIcon />, key: 'general', translationKey: 'userSettings.general' as TranslationKey },
  { icon: <ManageAccountsOutlinedIcon />, key: 'password', translationKey: 'userSettings.password' as TranslationKey },
  { icon: <EmailIcon />, key: 'email', translationKey: 'userSettings.email' as TranslationKey },
  { icon: <BlockIcon />, key: 'blockedOrgs', translationKey: 'emailPreferences.title' as TranslationKey },
  { icon: <LogoutIcon />, key: 'logout', translationKey: 'userSettings.logout' as TranslationKey },
];

const UserSettingsModal = ({
  open,
  menuSelected,
  setMenuSelected,
  close,
}: {
  menuSelected: UserSettingsMenuKey;
  setMenuSelected: (key: UserSettingsMenuKey) => void;
  open: boolean;
  close: () => void;
}) => {
  const t = useTranslations();
  const { i18n } = useTranslation();
  const isDesktop = useBreakpointUp('sm');

  const { signOut } = useAuth();
  const { user: clerkUser } = useUser();
  const notifications = useNotifications();
  const { mutateAsync } = useUpdateUserMutation();
  const user = useUserStore((s) => s.user);
  const setActiveOrganization = useActiveOrganizationStore((s) => s.setActiveOrganization);

  const updateUserSchema = updateUserSchemaFactory(t);
  type UpdateUser = z.input<typeof updateUserSchema>;

  const deriveGeneral = useCallback(
    (): UpdateUser['general'] => ({
      firstName: user?.firstName ?? clerkUser?.firstName ?? '',
      lastName: user?.lastName ?? clerkUser?.lastName ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      language: user?.language ?? i18n.language,
    }),
    [user, clerkUser, i18n.language],
  );

  const form = useForm<UpdateUser>({
    resolver: zodResolver(updateUserSchema),
    mode: 'onChange',
    defaultValues: {
      general: deriveGeneral(),
    },
  });

  useEffect(() => {
    const general = deriveGeneral();
    form.setValue('general.firstName', general.firstName, { shouldDirty: false });
    form.setValue('general.lastName', general.lastName, { shouldDirty: false });
    form.setValue('general.phoneNumber', general.phoneNumber, { shouldDirty: false });
    form.setValue('general.language', general.language, { shouldDirty: false });
  }, [deriveGeneral, form]);

  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuSelect = (key: UserSettingsMenuKey) => {
    if (key === 'logout') {
      handleLogout();
    } else {
      setMenuSelected(key);
    }
  };

  const handleLogout = () => {
    signOut();
    setActiveOrganization(undefined);
    close();
  };

  const tabContent = ({
    type,
    tabTitle,
    form,
  }: {
    type: UserSettingsMenuKey;
    tabTitle?: TypographyProps;
    form: UseFormReturn<UpdateUser>;
  }) => {
    switch (type) {
      case 'general':
        return (
          <GeneralTab
            form={form}
            tabTitle={tabTitle}
            onSubmit={form.handleSubmit(onSubmit)}
            onCancel={() => form.reset({ general: deriveGeneral() })}
          />
        );
      case 'password':
        return <PasswordResetTab tabTitle={tabTitle} />;
      case 'email':
        return <EmailManagementTab tabTitle={tabTitle} />;
      case 'blockedOrgs':
        return <BlockedOrganizationsTab tabTitle={tabTitle} />;
      default:
        return null;
    }
  };

  const onSubmit = async (data: UpdateUser) => {
    try {
      // Get raw form value before transform to distinguish cleared field from never-set field
      // Schema transform converts empty strings to undefined, so we check raw value
      const rawPhoneNumber = form.getValues('general.phoneNumber');
      // Send null explicitly to clear phone number when empty; undefined will keep the existing value
      const phoneNumberToSend = rawPhoneNumber === '' ? null : data.general.phoneNumber;

      const updated = await mutateAsync({
        firstName: data.general.firstName,
        lastName: data.general.lastName,
        phoneNumber: phoneNumberToSend,
        language: data.general.language,
      });

      // Reset form with updated values from server response
      form.reset({
        general: {
          firstName: updated.firstName ?? '',
          lastName: updated.lastName ?? '',
          phoneNumber: updated.phoneNumber ?? '',
          language: updated.language ?? i18n.language,
        },
      });

      notifications.notifySuccess(t('user.notification.updateSuccess'));

      if (data.general.language) {
        i18n.changeLanguage(data.general.language);
      }
    } catch {
      notifications.notifyError(t('user.notification.updateError'));
    }
  };

  const displayName = [user?.firstName ?? clerkUser?.firstName, user?.lastName ?? clerkUser?.lastName]
    .filter(Boolean)
    .join(' ');

  // Filter out logout from menuButtons for StandardModal (on mobile, logout is in the footer)
  const nonLogoutButtons = menuButtons.filter((btn) => btn.key !== 'logout');

  const footer = !isDesktop ? (
    <Button onClick={handleLogout} variant="outlined" color="error" size="small" startIcon={<LogoutIcon />}>
      {t('userSettings.logout')}
    </Button>
  ) : undefined;

  return (
    <StandardModal<UserSettingsMenuKey>
      open={open}
      close={close}
      title={t('userSettings.title')}
      menuButtons={nonLogoutButtons}
      menuSelected={menuSelected === 'logout' ? 'general' : (menuSelected as Exclude<UserSettingsMenuKey, 'logout'>)}
      onMenuOpen={(open) => setMenuOpen(open)}
      onMenuSelect={handleMenuSelect}
      topLeftSection={
        <Box
          onClick={() => setMenuOpen(!menuOpen)}
          sx={{
            padding: '8px 0px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ marginRight: '16px' }}>
            <ClerkProfilePhotoEditor avatarSize={40} />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: '150px',
              }}
            >
              {displayName}
            </Typography>
          </Box>
        </Box>
      }
      footer={footer}
    >
      {!isDesktop && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <ClerkProfilePhotoEditor avatarSize={56} />
          <Typography variant="body1" fontWeight={500} noWrap>
            {displayName}
          </Typography>
        </Box>
      )}
      {tabContent({ type: menuSelected, form })}
    </StandardModal>
  );
};

export default UserSettingsModal;
